import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { Nav, NavItem, NavLink, Alert } from 'reactstrap';
import { CheckCircle, AlertCircle } from 'lucide-react';
import classnames from 'classnames';
import { clearExportState } from '../../store/reporting/actions';
import ExportWorkOrdersForm from './ExportWorkOrdersForm';
import ExportAssetsForm from './ExportAssetsForm';
import ExportLocationsForm from './ExportLocationsForm';
import ExportRequestsForm from './ExportRequestsForm';
import ExportComingSoon from './ExportComingSoon';
import { useTranslation } from 'react-i18next';

const ExportDataTab = ({ organizationId, mode = 'org' }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { exportLoading, exportError, exportSuccess } = useSelector(state => state.Reporting);
  const [activeSubTab, setActiveSubTab] = useState('work-orders');

  const SUB_TABS = useMemo(() => [
    { key: 'work-orders', label: t('shared.work_orders', 'Work Orders') },
    { key: 'assets', label: t('shared.assets', 'Assets') },
    { key: 'locations', label: t('shared.locations', 'Locations') },
    { key: 'requests', label: t('shared.requests', 'Requests') },
    { key: 'asset-status', label: t('shared.asset_status', 'Asset Status'), comingSoon: true },
    { key: 'parts', label: t('shared.parts', 'Parts'), comingSoon: true },
    { key: 'meters', label: t('shared.meters', 'Meters'), comingSoon: true },
    { key: 'vendors', label: t('shared.vendors', 'Vendors'), comingSoon: true },
    { key: 'time-cost', label: t('shared.time_cost', 'Time & Cost'), comingSoon: true },
    { key: 'labor', label: t('shared.labor_utilization', 'Labor Utilization'), comingSoon: true },
  ], [t]);

  // Clear alerts after 5 seconds
  useEffect(() => {
    if (exportSuccess || exportError) {
      const timer = setTimeout(() => {
        dispatch(clearExportState());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [exportSuccess, exportError, dispatch]);

  // Clear export state when switching sub-tabs
  useEffect(() => {
    dispatch(clearExportState());
  }, [activeSubTab, dispatch]);

  const renderSubTabContent = () => {
    const tab = SUB_TABS.find(tObject => tObject.key === activeSubTab);
    if (tab?.comingSoon) {
      return <ExportComingSoon title={`${t('shared.export', 'Export')} ${tab.label}`} />;
    }

    switch (activeSubTab) {
      case 'work-orders':
        return <ExportWorkOrdersForm organizationId={organizationId} mode={mode} />;
      case 'assets':
        return <ExportAssetsForm organizationId={organizationId} mode={mode} />;
      case 'locations':
        return <ExportLocationsForm organizationId={organizationId} mode={mode} />;
      case 'requests':
        return <ExportRequestsForm organizationId={organizationId} mode={mode} />;
      default:
        return null;
    }
  };

  return (
    <div>
      {/* Success/Error Alerts */}
      <AnimatePresence>
        {exportSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <Alert color="success" className="d-flex align-items-center gap-2">
              <CheckCircle size={18} />
              {t('reporting.export.success_message', 'Export downloaded successfully.')}
            </Alert>
          </motion.div>
        )}
        {exportError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <Alert color="danger" className="d-flex align-items-center gap-2">
              <AlertCircle size={18} />
              {exportError}
            </Alert>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sub-tab Navigation */}
      <Nav className="export-sub-tabs nav-pills flex-wrap gap-1 mb-4">
        {SUB_TABS.map((tab) => {
          const isActive = activeSubTab === tab.key;
          return (
            <NavItem key={tab.key}>
              <NavLink
                className={classnames(
                  'px-3 py-2 rounded-pill',
                  { active: isActive }
                )}
                onClick={() => setActiveSubTab(tab.key)}
                style={{
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  backgroundColor: isActive ? '#3980c0' : '#fff',
                  color: isActive ? '#fff' : '#6c757d',
                  border: isActive ? 'none' : '1px solid #e0e0e0',
                  ...(tab.comingSoon && !isActive ? { opacity: 0.6 } : {}),
                }}
              >
                {tab.label}
                {tab.comingSoon && (
                  <span
                    className="badge ms-1"
                    style={{
                      fontSize: '0.65rem',
                      backgroundColor: isActive ? 'rgba(255,255,255,0.25)' : '#f0f0f0',
                      color: isActive ? '#fff' : '#999',
                    }}
                  >
                    {t('shared.soon', 'Soon')}
                  </span>
                )}
              </NavLink>
            </NavItem>
          );
        })}
      </Nav>

      {/* Sub-tab Content */}
      <div className="bg-white rounded-3 shadow-sm border" style={{ overflow: 'visible' }}>
        {renderSubTabContent()}
      </div>
    </div>
  );
};

export default ExportDataTab;
